import { useCallback } from 'react';
import { apiFetch } from '@/lib/api/apiClient';
import type { WebSocketClient } from '@/lib/ws/wsClient';
import { useBoardEditorStore } from '@/stores/useBoardEditorStore';
import type { UploadOverlayStatus } from '@/stores/useBoardEditorStore';

interface ESP32Artifact {
    filename: string;
    address: string;
    data_b64: string;
}

interface UseBoardUploadOptions {
    board: 'orange' | 'blue';
    code: string;
    port: string | null;
    blockXml: string | null;
    isBoardConnected: boolean;
    isUploading: boolean;
    wsRef: React.RefObject<WebSocketClient | null>;
    useWebSerial: boolean;
    onUploadStart: () => void;
    setActiveRequestId: (id: string) => void;
    onUploadEvent: (e: {
        source: 'cloud';
        kind: 'phase' | 'result';
        status?: UploadOverlayStatus;
        message?: string;
        success?: boolean;
        errorMessage?: string;
        requestId?: string;
    }) => void;

    onLog: (message: string) => void;
    onError: (error: unknown) => void;
    onWebSerialApiUpload?: (
        firmwareData: string,
        firmwareType: 'hex' | 'bin',
        esp32Artifacts: ESP32Artifact[] | null,
    ) => Promise<void>;
}

interface UseBoardUploadReturn {
    handleUpload: () => void;
}

// コンパイル待ち人数表示のポーリング間隔。数秒〜数十秒程度の待ちが対象なので、
// この間隔で十分体感上の遅延なく更新できる(要相談の上で決定した粒度)。
const QUEUE_POLL_INTERVAL_MS = 2000;

function makeRequestId() {
    const t = Date.now();
    const r = crypto.getRandomValues(new Uint32Array(2));
    return `${t}-${r[0].toString(16)}${r[1].toString(16)}`;
}

export function useBoardUpload(options: UseBoardUploadOptions): UseBoardUploadReturn {
    const {
        board,
        code,
        port,
        blockXml,
        isBoardConnected,
        isUploading,
        wsRef,
        useWebSerial,
        onUploadStart,
        setActiveRequestId,
        onUploadEvent,
        onLog,
        onError,
        onWebSerialApiUpload,
    } = options;

    const handleUpload = useCallback(async () => {
        if (!isBoardConnected || !port) {
            onLog('⚠️Board not connected');
            return;
        }
        if (isUploading) return;
        const ws = wsRef.current;
        if (!useWebSerial) {
            if (!ws || !ws.isConnected) {
                onLog('⚠️WebSocket not connected');
                onError(new Error('WebSocket not connected'));
                return;
            }
        } else {
            if (!onWebSerialApiUpload) {
                onLog('⚠️Web Serial upload function missing');
                onError(new Error('Web Serial upload function missing'));
                return;
            }
        }

        const requestId = makeRequestId();
        setActiveRequestId(requestId);
        onUploadStart();

        onUploadEvent({
            source: 'cloud',
            kind: 'phase',
            status: 'checking',
            message: 'Bridge接続確認中...',
            requestId,
        });
        onLog('スクーミーボードに書き込みを開始しました。');

        try {
            onUploadEvent({
                source: 'cloud',
                kind: 'phase',
                status: 'compiling',
                message: 'コンパイル中...',
                requestId,
            });
            onLog('🤖コンパイル中・・・');

            // ALBのアイドルタイムアウト対策。1本のHTTP接続を長時間開いたままにしないよう、
            // 投げたら即座にjob_idだけ受け取り、結果は短い間隔でポーリングして取得する。
            const { job_id: jobId } = await apiFetch('/bridge/build/submit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    board_type: board,
                    board_code: code,
                }),
            });
            useBoardEditorStore.getState().setActiveCompileJobId(jobId);

            // job_idの作成順から「自分より前にまだ終わっていない件数」を正確に数えられるので、
            // 同じポーリングでその値(ahead)も受け取ってバッジ表示に使う。
            let compileData;
            for (;;) {
                const statusResp = await apiFetch(`/bridge/build/status/${jobId}`);
                if (statusResp.status === 'done') {
                    compileData = statusResp.result;
                    break;
                }
                if (statusResp.status === 'error') {
                    throw new Error(statusResp.message || 'コンパイルに失敗しました');
                }
                if (statusResp.status === 'cancelled') {
                    // ユーザーが順番待ちボタンでキャンセルした場合。失敗ではないのでエラー表示はせず、
                    // 静かにアップロード表示自体を終了する。
                    onLog('🚫コンパイルの順番待ちをキャンセルしました。');
                    const store = useBoardEditorStore.getState();
                    store.setActiveCompileJobId(null);
                    store.setIsUploading(false);
                    store.resetUpload();
                    return;
                }
                const st = useBoardEditorStore.getState();
                if (st.uploadStatus === 'compiling') {
                    const ahead: number = statusResp.ahead ?? 0;
                    st.setUploadQueueAhead(ahead > 0 ? ahead : null);
                }
                await new Promise((resolve) => setTimeout(resolve, QUEUE_POLL_INTERVAL_MS));
            }
            useBoardEditorStore.getState().setUploadQueueAhead(null);
            useBoardEditorStore.getState().setActiveCompileJobId(null);

            const firmwareData = compileData.artifact_b64;
            const filename = compileData.filename;
            const firmwareType = filename.endsWith('.hex') ? 'hex' : 'bin';
            const esp32Artifacts: ESP32Artifact[] | null = compileData.esp32_artifacts ?? null;

            onUploadEvent({
                source: 'cloud',
                kind: 'phase',
                status: 'compiled',
                message: 'コンパイル完了',
                requestId,
            });
            onLog(`🔨コンパイル完了`);

            if (!useWebSerial && ws) {
                onUploadEvent({
                    source: 'cloud',
                    kind: 'phase',
                    status: 'sending',
                    message: 'ブリッジに送信完了',
                    requestId,
                });
                onLog('🚀ブリッジに送信完了');
                ws.send({
                    type: 'upload_firmware',
                    request_id: requestId,
                    firmware_data: firmwareData,
                    firmware_type: firmwareType,
                    port,
                    board_type: board,
                    esp32_artifacts: esp32Artifacts,
                    block_xml: blockXml ?? undefined,
                });
                onLog('⚡書き込み中・・・');
            } else {
                if (!onWebSerialApiUpload) return;
                onLog('⚡書き込み中・・・');
                await onWebSerialApiUpload(firmwareData, firmwareType, esp32Artifacts);
            }
        } catch (error) {
            useBoardEditorStore.getState().setUploadQueueAhead(null);
            useBoardEditorStore.getState().setActiveCompileJobId(null);
            onUploadEvent({
                source: 'cloud',
                kind: 'result',
                success: false,
                errorMessage: 'コンパイルに失敗しました',
                requestId,
            });

            onLog(`☔書き込みに失敗しました。${error instanceof Error ? error.message : String(error)}`);
            onError(error);
        }
    }, [
        isBoardConnected,
        port,
        board,
        code,
        blockXml,
        isUploading,
        wsRef,
        useWebSerial,
        onUploadStart,
        setActiveRequestId,
        onUploadEvent,
        onLog,
        onError,
        onWebSerialApiUpload,
    ]);

    return { handleUpload };
}
